import { SERVICES, SERVICES_EXTRA, TRUST_STATS, PROCESS_STEPS, MEDIA, TESTIMONIALS } from "../../data/site";
import { Check, Award, Shield, MapPin, Clock, Layers, PaintBucket, ArrowRight } from "lucide-react";

const ICONS = { Layers, PaintBucket };

export function Services() {
  return (
    <section id="services" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl reveal">
          <span className="overline">What we build</span>
          <h2 className="font-serif-display mt-5 text-4xl sm:text-5xl md:text-6xl text-[#F5F5F0] leading-tight">
            Three services. <span className="italic text-champagne">One standard.</span>
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg max-w-2xl font-sans-body">
            We don't take shortcuts and we don't subcontract the parts that matter. Every project — from a five-day shower
            swap to a full re-build — is run by the same crew, to the same standard.
          </p>
        </div>

        <div className="mt-20 md:mt-28 space-y-28 md:space-y-40">
          {SERVICES.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <article
                key={s.id}
                id={s.id}
                className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center reveal`}
              >
                <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[5/4] overflow-hidden rounded-sm group">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-white/5" />
                  </div>
                </div>
                <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
                  <span className="overline">{s.overline}</span>
                  <h3 className="font-serif-display mt-4 text-3xl sm:text-4xl md:text-5xl text-[#F5F5F0] leading-tight">
                    {s.title}
                  </h3>
                  <div className="mt-3 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-champagne font-sans-body">
                    <Clock className="w-3.5 h-3.5" /> {s.duration}
                  </div>
                  <p className="mt-6 text-[#A3A3A3] leading-relaxed font-sans-body">{s.description}</p>
                  <ul className="mt-8 space-y-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[#F5F5F0]/85 text-[15px] font-sans-body">
                        <Check className="w-4 h-4 text-champagne mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#estimate"
                    data-testid={`service-${s.id}-cta`}
                    className="mt-10 inline-block text-[12px] tracking-[0.22em] uppercase text-champagne hover:text-[#D4AF37] underline-grow font-sans-body font-semibold"
                  >
                    Request a quote for {s.title.split(" ")[0].toLowerCase()}
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function AdditionalServices() {
  return (
    <section id="additional-services" className="py-20 md:py-28 border-t border-white/5 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl reveal">
          <span className="overline">Also offered</span>
          <h2 className="font-serif-display mt-5 text-3xl sm:text-4xl md:text-5xl text-[#F5F5F0] leading-tight">
            Beyond the bathroom.
          </h2>
          <p className="mt-5 text-[#A3A3A3] leading-relaxed max-w-xl font-sans-body">
            Add-ons that pair naturally with any renovation — or standalone jobs booked on their own schedule.
          </p>
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-2 gap-px bg-[#262626]">
          {SERVICES_EXTRA.map((s) => {
            const Icon = ICONS[s.icon];
            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={`extra-service-${s.id}`}
                className="bg-[#0B0B0B] p-8 md:p-12 reveal hover:bg-[#0F0F0F] transition-colors duration-500 group"
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-6 h-6 text-champagne" />
                  <span className="overline">{s.id === "drywall" ? "04" : "05"}</span>
                </div>
                <h3 className="font-serif-display mt-8 text-3xl md:text-4xl text-[#F5F5F0] leading-tight">
                  {s.title}
                </h3>
                <p className="mt-2 text-champagne italic font-serif-display text-lg md:text-xl">{s.tagline}</p>
                <p className="mt-5 text-[#A3A3A3] leading-relaxed font-sans-body">{s.description}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[#F5F5F0]/85 text-[15px] font-sans-body">
                      <Check className="w-4 h-4 text-champagne mt-1 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#estimate"
                  data-testid={`extra-${s.id}-cta`}
                  className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-champagne hover:text-[#D4AF37] font-sans-body font-semibold underline-grow"
                >
                  Request a quote
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function WhyUs() {
  const icons = [Award, Shield, Clock, MapPin];
  return (
    <section className="py-24 md:py-32 border-t border-white/5 bg-[#0C0C0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-5 reveal">
            <span className="overline">Why Cresta</span>
            <h2 className="font-serif-display mt-5 text-4xl sm:text-5xl text-[#F5F5F0] leading-tight">
              Quiet confidence,<br /><span className="italic text-champagne">loud results.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-[#262626]">
              {TRUST_STATS.map((s, i) => {
                const Icon = icons[i % icons.length];
                return (
                  <div key={s.label} className="bg-[#0C0C0C] p-8 md:p-10 reveal">
                    <Icon className="w-5 h-5 text-champagne" />
                    <div className="font-serif-display text-5xl md:text-6xl text-[#F5F5F0] mt-6 leading-none">
                      {s.value}
                    </div>
                    <p className="mt-4 text-[#A3A3A3] text-sm leading-relaxed max-w-xs font-sans-body">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Process() {
  return (
    <section id="process" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32 reveal">
            <span className="overline">How it works</span>
            <h2 className="font-serif-display mt-5 text-4xl sm:text-5xl md:text-6xl text-[#F5F5F0] leading-tight">
              Six steps,<br /><span className="italic text-champagne">no surprises.</span>
            </h2>
            <p className="mt-6 text-[#A3A3A3] leading-relaxed max-w-md font-sans-body">
              From first measurement to final walkthrough — a process built around clear communication, fixed pricing,
              and obsessive attention to the work behind the wall.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-3">
              <img src={MEDIA.process[0]} alt="Cresta installation in progress" className="aspect-square object-cover rounded-sm" loading="lazy" />
              <img src={MEDIA.process[1]} alt="Laser-leveled tile installation" className="aspect-square object-cover rounded-sm" loading="lazy" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <ol className="space-y-px bg-[#262626]">
              {PROCESS_STEPS.map((p) => (
                <li key={p.n} className="bg-[#0A0A0A] p-8 md:p-10 reveal flex gap-6 md:gap-10 items-start hover:bg-[#111] transition-colors">
                  <span className="font-serif-display text-champagne text-2xl md:text-3xl shrink-0 w-12">{p.n}</span>
                  <div>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#F5F5F0]">{p.title}</h3>
                    <p className="mt-2 text-[#A3A3A3] leading-relaxed font-sans-body">{p.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Portfolio() {
  return (
    <section id="portfolio" className="py-24 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
          <div>
            <span className="overline">Selected work</span>
            <h2 className="font-serif-display mt-5 text-4xl sm:text-5xl md:text-6xl text-[#F5F5F0] leading-tight">
              Recent <span className="italic text-champagne">bathrooms.</span>
            </h2>
          </div>
          <a
            href="#estimate"
            data-testid="portfolio-cta"
            className="text-[12px] tracking-[0.22em] uppercase text-champagne hover:text-[#D4AF37] underline-grow font-sans-body font-semibold"
          >
            Start your project →
          </a>
        </div>

        <div className="mt-12 md:mt-16 columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4 [column-fill:_balance]">
          {MEDIA.gallery.map((g, i) => (
            <figure
              key={i}
              data-testid={`gallery-item-${i}`}
              className="relative group overflow-hidden rounded-sm mb-3 md:mb-4 break-inside-avoid"
            >
              <img
                src={g.src}
                alt={`${g.tag} project ${i + 1}`}
                className="w-full h-auto block object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute bottom-3 left-3 right-3 text-[10px] tracking-[0.25em] uppercase text-[#F5F5F0] font-sans-body opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="text-champagne">{g.tag}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section id="testimonials" className="py-24 md:py-36 bg-[#0C0C0C] border-t border-white/5">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <span className="overline reveal">Homeowner words</span>
        <div className="mt-10 space-y-20 md:space-y-28">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={i} className="reveal">
              <p className="font-serif-display italic text-2xl sm:text-3xl md:text-4xl text-[#F5F5F0] leading-snug">
                <span className="text-champagne">&ldquo;</span>
                {t.quote}
                <span className="text-champagne">&rdquo;</span>
              </p>
              <footer className="mt-6 text-[12px] tracking-[0.22em] uppercase text-[#A3A3A3] font-sans-body">
                {t.author} <span className="text-[#262626] mx-2">·</span> {t.location}
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
